import React, { useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap"
import { Button } from "reactstrap"
import { Editor } from "primereact/editor"
import { InputText } from "primereact/inputtext"
import { Toast } from "primereact/toast"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import NewsAndArticleApi from "Api/NewsAndArticleApi"
import S3UploadApi from "Api/s3Upload"
import "./AddNewsAndArticle.scss"

const AddNewsAndArticle = () => {
  const navigate = useNavigate()
  const toast = useRef(null)

  const [articleData, setArticleData] = useState({
    title: "",
    content: "",
    image: "",
    author: "",
    position: "",
  })

  const [imageFile, setImageFile] = useState(null)
  const [imageUrl, setImageUrl] = useState("")
  const [isUploading, setIsUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "News and Articles", link: "/news-and-articles" },
    { label: "Add Article", link: "#" },
  ]

  const handleInputChange = (name, value) => {
    setArticleData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleImageChange = e => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0])
      setImageUrl("")
    }
  }

  const uploadImage = async () => {
    if (!imageFile) return null

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append("image", imageFile)

      const response = await S3UploadApi.uploadImage(formData)
      const newImageUrl = response.data.imageUrl

      setImageUrl(newImageUrl)
      handleInputChange("image", newImageUrl)
      setImageFile(null)
      setIsUploading(false)
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Image uploaded successfully",
        life: 3000,
      })
      return newImageUrl
    } catch (error) {
      console.error("Error uploading image:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error uploading image",
        life: 3000,
      })
      setIsUploading(false)
      return null
    }
  }

  const handleImageDelete = async () => {
    if (imageUrl) {
      try {
        await S3UploadApi.deleteImage({ fileUrl: imageUrl })
        setImageUrl("")
        handleInputChange("image", "")
        const fileInput = document.getElementById("image")
        if (fileInput) {
          fileInput.value = ""
        }
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Image deleted successfully",
          life: 3000,
        })
      } catch (error) {
        console.error("Error deleting image:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete image",
          life: 3000,
        })
      }
    }
  }

  const handleSubmit = async () => {
    if (!articleData.title || !articleData.content || !articleData.image) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "All fields are required",
        life: 3000,
      })
      return
    }

    setLoading(true)
    try {
      const payload = {
        title: articleData.title,
        content: articleData.content,
        image: articleData.image,
        authorDetails: {
          name: articleData.author,
          position: articleData.position,
        },
      }

      const response = await NewsAndArticleApi.AddNewsAndArticle(payload)

      if (response.data.success) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Article created successfully",
          life: 3000,
        })
        navigate("/news-and-articles")
      } else {
        throw new Error(response.data.message || "Failed to create article")
      }
    } catch (error) {
      console.error("Error creating article:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to create article",
        life: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="add-article-page">
      <Toast ref={toast} />
      <Container>
        <Row>
          <Col xs="12">
            <div className="mb-4 d-flex justify-content-between align-items-center">
              <CustomBreadCrumb
                data={breadcrumbItems}
                pageTitle="Add News & Article"
              />
            </div>

            <Card>
              <CardBody>
                <div className="mb-4">
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <InputText
                    id="title"
                    value={articleData.title}
                    onChange={e => handleInputChange("title", e.target.value)}
                    className="w-100"
                    placeholder="Enter article title"
                  />
                </div>

                <div className="d-flex gap-2">
                  <div className="mb-4 flex-grow-1">
                    <label htmlFor="title" className="form-label">
                      Author
                    </label>
                    <InputText
                      id="title"
                      value={articleData.author}
                      onChange={e =>
                        handleInputChange("author", e.target.value)
                      }
                      className="w-100"
                      placeholder="Enter author name"
                    />
                  </div>

                  <div className="mb-4 flex-grow-1">
                    <label htmlFor="title" className="form-label">
                      Position
                    </label>
                    <InputText
                      id="title"
                      value={articleData.position}
                      onChange={e =>
                        handleInputChange("position", e.target.value)
                      }
                      className="w-100"
                      placeholder="Enter author position"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="image" className="form-label">
                    Featured Image
                  </label>
                  <div className="image-upload-container">
                    {imageUrl && (
                      <div className="mb-2 position-relative">
                        <img
                          src={imageUrl}
                          alt="Article"
                          className="preview-image"
                        />
                        <Button
                          type="button"
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          style={{ margin: "2px" }}
                          onClick={handleImageDelete}
                        >
                          ×
                        </Button>
                      </div>
                    )}
                    {imageFile && (
                      <div className="mb-2">
                        <small>Selected file: {imageFile.name}</small>
                      </div>
                    )}
                    <div className="d-flex gap-2 align-items-center">
                      <input
                        type="file"
                        id="image"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="form-control"
                      />
                      {imageFile && !isUploading && !imageUrl && (
                        <Button
                          type="button"
                          color="primary"
                          size="sm"
                          onClick={uploadImage}
                        >
                          Upload
                        </Button>
                      )}
                    </div>
                    {isUploading && (
                      <small className="d-block mt-1">
                        <Spinner size="sm" /> Uploading image...
                      </small>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="content" className="form-label">
                    Content
                  </label>
                  <Editor
                    value={articleData.content}
                    onTextChange={e =>
                      handleInputChange("content", e.htmlValue)
                    }
                    style={{ height: "320px" }}
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => navigate("/news-and-articles")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      "Create Article"
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default AddNewsAndArticle
